import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faSpinner,
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf,
  faSyncAlt,
  faAngleLeft,
  faAngleRight,
  faDownload,
  faSearch,
  faUpload,
  faTableList,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import { getDprValidationUploadHistory, getDprValidationUploaders, refreshDprValidationSignedUrl } from "../../services/dprValidationUploadApi";

const STATUS_MAP = {
  uploaded:   { label: "Uploaded",   color: "bg-blue-100 text-blue-800",      icon: faFile },
  processing: { label: "Processing", color: "bg-yellow-100 text-yellow-800",  icon: faSpinner, spin: true },
  completed:  { label: "Completed",  color: "bg-emerald-100 text-emerald-800", icon: faCheckCircle },
  failed:     { label: "Failed",     color: "bg-red-100 text-red-800",        icon: faTimesCircle },
};

const PER_PAGE_OPTIONS = [10, 25, 50];

const formatBytes = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const formatDate = (dt) => {
  if (!dt) return "—";
  return new Date(dt).toLocaleString();
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || { label: status, color: "bg-gray-100 text-gray-700", icon: faHourglassHalf };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${s.color}`}>
      <FontAwesomeIcon icon={s.icon} spin={!!s.spin} size="xs" />
      {s.label}
    </span>
  );
};

const DprUploadValidationCsvHistoryPage = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [uploadedByFilter, setUploadedByFilter] = useState("");
  const [uploaderOptions, setUploaderOptions] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  // Track per-row refreshing state and refreshed URLs
  const [refreshingMap, setRefreshingMap] = useState({});
  const [refreshedUrls, setRefreshedUrls] = useState({});

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDprValidationUploadHistory({
        page,
        per_page: perPage,
        search: appliedSearch,
        uploaded_by: uploadedByFilter,
        status: statusFilter,
        date_from: dateFrom,
        date_to: dateTo,
        sort_by: sortBy,
        sort_direction: sortDir,
      });
      const d = res.data;
      setRows(d.data || []);
      setTotal(d.total || 0);
      setTotalPages(d.total_pages || 1);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, appliedSearch, uploadedByFilter, statusFilter, dateFrom, dateTo, sortBy, sortDir]);

  useEffect(() => {
    const fetchUploaders = async () => {
      try {
        const response = await getDprValidationUploaders();
        setUploaderOptions(Array.isArray(response?.data?.data) ? response.data.data : []);
      } catch {
        setUploaderOptions([]);
      }
    };
    fetchUploaders();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Auto-refresh while any row is processing
  useEffect(() => {
    const hasProcessing = rows.some((r) => r.status === "processing" || r.status === "uploaded");
    if (!hasProcessing) return;
    const timer = setInterval(fetchHistory, 8000);
    return () => clearInterval(timer);
  }, [rows, fetchHistory]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
    setPage(1);
  };

  const handleApplySearch = () => {
    setAppliedSearch(search.trim());
    setPage(1);
  };

  const handleClear = () => {
    setSearch("");
    setAppliedSearch("");
    setUploadedByFilter("");
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
    setSortBy("created_at");
    setSortDir("desc");
    setPage(1);
  };

  const handleRefreshUrl = async (uploadId) => {
    setRefreshingMap((prev) => ({ ...prev, [uploadId]: true }));
    try {
      const res = await refreshDprValidationSignedUrl(uploadId);
      const data = res?.data || {};
      if (data.signed_url) {
        setRefreshedUrls((prev) => ({
          ...prev,
          [uploadId]: { url: data.signed_url, refreshed: !!data.refreshed },
        }));
      }
    } catch {
      // no-op — leave old link visible
    } finally {
      setRefreshingMap((prev) => ({ ...prev, [uploadId]: false }));
    }
  };

  const SortIndicator = ({ col }) => {
    if (sortBy !== col) return <span className="ml-1 text-gray-300">⇅</span>;
    return <span className="ml-1 text-sky-500">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-5">

        {/* Header */}
        <div className="rounded-2xl bg-gradient-to-r from-cyan-600 via-sky-600 to-blue-600 p-6 text-white shadow-lg">
          <h1 className="text-2xl font-bold tracking-tight">(DPR) Upload Validation CSV History</h1>
          <p className="mt-1 text-sm text-sky-100">Browse all uploaded validation CSV batches and their processing status.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={() => navigate("/dpr-validation-upload")}
              className="inline-flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/30 transition"
            >
              <FontAwesomeIcon icon={faUpload} />
              Upload New CSV
            </button>
            <button
              onClick={() => navigate("/dpr-validation-upload-processed")}
              className="inline-flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/30 transition"
            >
              <FontAwesomeIcon icon={faTableList} />
              View Processed Rows
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-2xl border border-sky-100 bg-white p-5 shadow-sm">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Search Filename</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <FontAwesomeIcon icon={faSearch} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleApplySearch()}
                    placeholder="filename..."
                    className="w-full rounded-lg border border-gray-200 py-2 pl-8 pr-3 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                  />
                </div>
                <button
                  onClick={handleApplySearch}
                  className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
                >
                  Go
                </button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Uploaded By</label>
              <select
                value={uploadedByFilter}
                onChange={(e) => { setUploadedByFilter(e.target.value); setPage(1); }}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              >
                <option value="">All Uploaders</option>
                {uploaderOptions.map((opt) => {
                  const fullName = (opt.full_name || "").trim();
                  const label = fullName ? `${fullName} (${opt.username})` : opt.username;
                  return (
                    <option key={opt.username} value={opt.username}>{label}</option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              >
                <option value="">All Statuses</option>
                {Object.entries(STATUS_MAP).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Date From</label>
              <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Date To</label>
              <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <button onClick={handleClear} className="text-xs text-gray-400 underline hover:text-red-500">
              Clear Filters
            </button>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>Rows per page:</span>
              {PER_PAGE_OPTIONS.map((n) => (
                <button
                  key={n}
                  onClick={() => { setPerPage(n); setPage(1); }}
                  className={`rounded px-2 py-0.5 font-semibold transition ${perPage === n ? "bg-sky-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-sky-50"}`}
                >
                  {n}
                </button>
              ))}
              <button onClick={fetchHistory} className="ml-2 rounded-lg bg-gray-100 px-2 py-1 text-gray-500 hover:bg-sky-50" title="Refresh">
                <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-sky-100 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="cursor-pointer px-4 py-3 text-left" onClick={() => handleSort("original_file_name")}>
                    Filename <SortIndicator col="original_file_name" />
                  </th>
                  <th className="cursor-pointer px-4 py-3 text-left" onClick={() => handleSort("uploaded_by")}>
                    Uploaded By <SortIndicator col="uploaded_by" />
                  </th>
                  <th className="px-4 py-3 text-left">File Type</th>
                  <th className="px-4 py-3 text-right">Size</th>
                  <th className="cursor-pointer px-4 py-3 text-left" onClick={() => handleSort("created_at")}>
                    Uploaded <SortIndicator col="created_at" />
                  </th>
                  <th className="cursor-pointer px-4 py-3 text-center" onClick={() => handleSort("status")}>
                    Status <SortIndicator col="status" />
                  </th>
                  <th className="cursor-pointer px-4 py-3 text-right" onClick={() => handleSort("total_rows")}>
                    Total <SortIndicator col="total_rows" />
                  </th>
                  <th className="cursor-pointer px-4 py-3 text-right" onClick={() => handleSort("success_count")}>
                    Success <SortIndicator col="success_count" />
                  </th>
                  <th className="cursor-pointer px-4 py-3 text-right" onClick={() => handleSort("failed_count")}>
                    Failed <SortIndicator col="failed_count" />
                  </th>
                  <th className="px-4 py-3 text-center">Download</th>
                  <th className="px-4 py-3 text-left">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      {Array.from({ length: 11 }).map((__, j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-3 rounded bg-gray-200" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-12 text-center text-gray-400">
                      No upload history found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 transition">
                      <td className="max-w-[220px] truncate px-4 py-3 font-medium text-gray-800" title={row.original_file_name}>
                        <FontAwesomeIcon icon={faFile} className="mr-2 text-sky-400" />
                        {row.original_file_name}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {row.uploader_full_name ? (
                          <div>
                            <p className="font-medium text-gray-700">{row.uploader_full_name}</p>
                            <p className="text-xs text-gray-400">{row.uploaded_by || "—"}</p>
                          </div>
                        ) : (
                          row.uploaded_by || "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500">{row.file_type || "—"}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{formatBytes(row.file_size)}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(row.created_at)}</td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-700">{row.total_rows ?? 0}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-600">{row.success_count ?? 0}</td>
                      <td className="px-4 py-3 text-right font-semibold text-red-500">{row.failed_count ?? 0}</td>
                      <td className="px-4 py-3 text-center">
                        {row.signed_url ? (
                          <div className="flex flex-col items-center gap-1">
                            <a
                              href={refreshedUrls[row.upload_id]?.url || row.signed_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 rounded-lg bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-600 hover:bg-sky-100"
                            >
                              <FontAwesomeIcon icon={faDownload} />
                              CSV
                            </a>
                            <button
                              onClick={() => handleRefreshUrl(row.upload_id)}
                              disabled={!!refreshingMap[row.upload_id]}
                              title="Refresh download link"
                              className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-600 hover:bg-amber-100 disabled:opacity-50"
                            >
                              <FontAwesomeIcon
                                icon={refreshingMap[row.upload_id] ? faSpinner : faLink}
                                spin={!!refreshingMap[row.upload_id]}
                              />
                              {refreshedUrls[row.upload_id]?.refreshed
                                ? "Refreshed"
                                : refreshingMap[row.upload_id]
                                ? "..."
                                : "Refresh Link"}
                            </button>
                          </div>
                        ) : "—"}
                      </td>
                      <td className="max-w-[200px] px-4 py-3 text-xs text-red-500" title={row.error_message || ""}>
                        {row.error_message
                          ? row.error_message.length > 60
                            ? row.error_message.substring(0, 60) + "..."
                            : row.error_message
                          : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && total > 0 && (
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
              <span>
                Showing {((page - 1) * perPage) + 1}–{Math.min(page * perPage, total)} of {total}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded px-2 py-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`rounded px-2.5 py-1 font-semibold ${p === page ? "bg-sky-600 text-white" : "hover:bg-gray-100"}`}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="rounded px-2 py-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default DprUploadValidationCsvHistoryPage;
